import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import "../../css/AuscultationPanel.css";

// interface AuscultationPanelProps {
//   icon: string;
//   title: string;
//   panelKey: string;
//   audioUrl?: string;
//   isOpen: boolean;
//   onToggle: () => void;

//   onChange: (
//     key: string,
//     data: { assessmentNote: string; hasConcerns: boolean | null },
//   ) => void;

//   hasError?: boolean;
// }

interface AuscultationPanelProps {
  icon: string;
  title: string;
  panelKey: string;
  audioUrl?: string;
  isOpen: boolean;
  onToggle: () => void;

  onChange: (
    key: string,
    data: { assessmentNote: string; hasConcerns: boolean | null },
  ) => void;

  hasError?: boolean;

  abdominalData?: {
    name: string;
    finding: string;
    pain: boolean;
    mass: boolean;
  }[];
}

const AuscultationPanel: React.FC<AuscultationPanelProps> = ({
  icon,
  title,
  panelKey,
  audioUrl,
  isOpen,
  onToggle,
  onChange,
  hasError,
  abdominalData = [],
}) => {
  const [assessment, setAssessment] = useState("");
  const [concernStatus, setConcernStatus] = useState<"none" | "no" | "yes">(
    "none",
  );

  /* ---------- EMIT DATA UP ---------- */
  useEffect(() => {
    onChange(panelKey, {
      assessmentNote: assessment.trim(),
      hasConcerns: concernStatus === "none" ? null : concernStatus === "yes",
    });
  }, [assessment, concernStatus, panelKey]);

  /* ---------- WAVESURFER ---------- */
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!isOpen || !audioUrl || !waveformRef.current) return;

    waveSurferRef.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#d1d5db",
      progressColor: "#ef4444",
      cursorColor: "#ef4444",
      barWidth: 2,
      barRadius: 2,
      height: 48,
      normalize: true,
    });

    waveSurferRef.current.on("ready", () => {
      setDuration(Math.floor(waveSurferRef.current!.getDuration()));
    });

    waveSurferRef.current.on("play", () => setIsPlaying(true));
    waveSurferRef.current.on("pause", () => setIsPlaying(false));
    waveSurferRef.current.on("finish", () => setIsPlaying(false));

    waveSurferRef.current.load(audioUrl);

    return () => {
      waveSurferRef.current?.destroy();
      waveSurferRef.current = null;
    };
  }, [isOpen, audioUrl]);

  /* ---------- MISSING FUNCTIONS (FIX) ---------- */
  const handlePlayPause = () => {
    waveSurferRef.current?.playPause();
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className={`auscultation-panel ${hasError ? "error" : ""}`}>
      <div className="panel-header" onClick={onToggle}>
        <div className="header-left">
          <span>{icon}</span>
          <strong>{title}</strong>
          {/* <span>Section: {panelKey}</span> */}
        </div>
        <span>▼</span>
      </div>

      {isOpen && (
        <div className="panel-body">
          <div className="audio-player">
            <button className="play-button" onClick={handlePlayPause}>
              {isPlaying ? "❚❚" : "▶"}
            </button>

            <div className="waveform-container">
              <div ref={waveformRef} />
            </div>

            <div className="timestamps">00:00 / {formatTime(duration)}</div>

            <div className="player-actions">
              {audioUrl && (
                <>
                  {/* <a
                    href={audioUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </a> */}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (waveSurferRef.current) {
                        waveSurferRef.current.stop();
                        waveSurferRef.current.play();
                      }
                    }}
                  >
                    Replay
                  </a>
                </>
              )}
            </div>
          </div>

          {/* ---------------- ABDOMINAL EXAMINATION ---------------- */}
          {panelKey === "abdomen" && abdominalData.length > 0 && (
            <div className="abdominal-section">
              <h3>Abdominal Examination (9 Regions)</h3>

              {abdominalData.map((region, index) => (
                <div key={index} className="abdominal-card">
                  <p>
                    <strong>
                      {index + 1}. {region.name}
                    </strong>
                  </p>

                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        checked={region.finding === "Expected Findings"}
                        readOnly
                      />
                      Expected Findings
                    </label>

                    <div className="unexpected-block">
                      <label>
                        <input
                          type="radio"
                          checked={region.finding === "Unexpected Findings"}
                          readOnly
                        />
                        Unexpected Findings
                      </label>

                      {region.finding === "Unexpected Findings" && (
                        <div className="checkbox-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={region.pain}
                              readOnly
                            />
                            Pain
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={region.mass}
                              readOnly
                            />
                            Mass
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="assessment-section">
            <p>
              <strong>Assessment</strong>
            </p>

            <textarea
              placeholder="Write a note if there are concerns..."
              value={assessment}
              onChange={(e) => setAssessment(e.target.value)}
            />

            <label>
              <input
                type="radio"
                name={`${panelKey}-concern`}
                onChange={() => setConcernStatus("no")}
              />
              No Concerns
            </label>

            <label>
              <input
                type="radio"
                name={`${panelKey}-concern`}
                onChange={() => setConcernStatus("yes")}
              />
              Concerns
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuscultationPanel;
